<script setup>
import { computed, onMounted, ref } from 'vue';
import { format } from 'date-fns';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import {
  isPostRemovable,
  postChannelType,
  postStatusLabelKey,
  postStatusTextColor,
  postTypeIcon,
  postTypeLabelKey,
} from 'dashboard/helper/wgptSocialMediaPosts';

import Breadcrumb from 'next/breadcrumb/Breadcrumb.vue';
import Button from 'next/button/Button.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Dialog from 'next/dialog/Dialog.vue';
import Icon from 'next/icon/Icon.vue';
import Spinner from 'next/spinner/Spinner.vue';
import GalleryView from 'components/widgets/conversation/components/GalleryView.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { isAdmin } = useAdmin();

const getPost = useMapGetter('wgptSocialMediaPosts/getSocialMediaPost');
const uiFlags = useMapGetter('wgptSocialMediaPosts/getUIFlags');
const getInbox = useMapGetter('inboxes/getInbox');

const post = computed(() => getPost.value(route.params.postId));
const isFetchingItem = computed(() => uiFlags.value.isFetchingItem);
// getInbox returns {} for a missing inbox - normalize to null so fallbacks fire.
const inbox = computed(() => {
  const record = getInbox.value(post.value.inbox_id);
  return record?.id ? record : null;
});
// When the inbox is gone, keep the channel branding by deriving it from the post type.
const channelIconInbox = computed(
  () => inbox.value || { channel_type: postChannelType(post.value) }
);
const externalUrl = computed(() => post.value.external_url);
// The platform link is only meaningful while the post is actually live.
const canOpenExternal = computed(
  () => post.value.status === 'published' && Boolean(externalUrl.value)
);

const mediaItems = computed(() =>
  (post.value.media_items || []).map((item, index) => ({
    url: item.url,
    isVideo: item.video,
    thumbnailUrl: item.thumbnail_url,
    message_id: index,
    data_url: item.url,
    file_type: item.video ? 'video' : 'image',
  }))
);

const showGallery = ref(false);
const selectedAttachment = ref(null);

const openGallery = item => {
  selectedAttachment.value = item;
  showGallery.value = true;
};

const breadcrumbItems = computed(() => [
  {
    label: t('WGPT_SOCIAL_MEDIA_POSTS.SIDEBAR.TITLE'),
    link: '#',
  },
  {
    label: post.value.caption,
  },
]);

const statusTextColor = computed(() => postStatusTextColor(post.value.status));
const statusText = computed(() =>
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  t(postStatusLabelKey(post.value.status))
);
const postTypeText = computed(() =>
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  t(postTypeLabelKey(post.value))
);
const postTypeIconName = computed(() => postTypeIcon(post.value));

const hasError = computed(
  () => post.value.status === 'failed' && !!post.value.error_message
);

const formatDate = value =>
  value ? format(new Date(value), 'LLL d, yyyy, h:mm a') : undefined;

const removeDialogRef = ref(null);
const isRemovable = computed(
  () => isAdmin.value && isPostRemovable(post.value)
);
const isRemoving = computed(() => uiFlags.value.isRemoving);

const handleRemove = async () => {
  try {
    await store.dispatch('wgptSocialMediaPosts/remove', post.value.id);
    removeDialogRef.value?.close();
    useAlert(t('WGPT_SOCIAL_MEDIA_POSTS.REMOVE.MESSAGES.SUCCESS'));
  } catch (error) {
    useAlert(error.message);
  }
};

// route.params.inboxId is 'all' when this post was opened from the All list rather than a
// specific channel (see PostCard.vue#goToDetails) - route back to wherever it came from instead
// of always landing on a channel-specific list.
const goToList = () => {
  router.push(
    route.params.inboxId === 'all'
      ? { name: 'social_media_posts_overview' }
      : {
          name: 'social_media_posts_inbox',
          params: { inboxId: route.params.inboxId },
        }
  );
};

const openExternal = () => {
  if (externalUrl.value) {
    window.open(externalUrl.value, '_blank', 'noopener,noreferrer');
  }
};

onMounted(() => {
  store.dispatch('inboxes/get');
  store.dispatch('wgptSocialMediaPosts/show', route.params.postId);
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6">
      <div class="w-full mx-auto max-w-[789px]">
        <div class="flex items-center justify-between w-full py-7 gap-2">
          <Breadcrumb :items="breadcrumbItems" @click="goToList" />
          <div class="flex items-center gap-2">
            <Button
              v-if="canOpenExternal"
              :label="t('WGPT_SOCIAL_MEDIA_POSTS.CONTEXT_MENU.OPEN_POST')"
              icon="i-lucide-external-link"
              size="sm"
              slate
              @click="openExternal"
            />
            <Button
              v-if="isRemovable"
              :label="t('WGPT_SOCIAL_MEDIA_POSTS.REMOVE.BUTTON')"
              icon="i-lucide-trash-2"
              color="ruby"
              size="sm"
              @click="removeDialogRef?.open()"
            />
          </div>
        </div>
      </div>
    </header>

    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full py-4 mx-auto max-w-[789px]">
        <div
          v-if="isFetchingItem"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>
        <div v-else class="flex flex-col gap-8 pb-6">
          <div>
            <div
              v-if="mediaItems.length"
              class="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4"
            >
              <button
                v-for="item in mediaItems"
                :key="item.url"
                type="button"
                class="relative w-full overflow-hidden rounded-lg aspect-square bg-n-slate-3 p-0"
                @click="openGallery(item)"
              >
                <img
                  v-if="item.isVideo && item.thumbnailUrl"
                  :src="item.thumbnailUrl"
                  class="object-cover w-full h-full"
                  alt=""
                />
                <video
                  v-else-if="item.isVideo"
                  :src="item.url"
                  class="object-cover w-full h-full pointer-events-none"
                  playsinline
                  preload="metadata"
                />
                <img
                  v-else
                  :src="item.url"
                  class="object-cover w-full h-full"
                  alt=""
                />
                <div
                  v-if="item.isVideo"
                  class="absolute inset-0 flex items-center justify-center bg-[rgba(0,0,0,0.2)]"
                >
                  <Icon
                    icon="i-lucide-play"
                    class="text-white size-8 drop-shadow"
                  />
                </div>
              </button>
            </div>
          </div>

          <div class="flex flex-col items-start gap-3">
            <span
              class="inline-flex items-center gap-2 text-sm text-n-slate-11"
            >
              {{
                `${t('WGPT_SOCIAL_MEDIA_POSTS.DETAILS.CREATED_AT', {
                  date: formatDate(post.created_at),
                })}`
              }}
              <span>•</span>
              <span
                class="font-medium inline-flex items-center h-5"
                :class="statusTextColor"
              >
                {{ statusText }}
              </span>
              <Icon
                v-if="hasError"
                v-tooltip.top="post.error_message"
                icon="i-lucide-circle-alert"
                class="flex-shrink-0 w-4 h-4 cursor-help text-n-ruby-11"
                :aria-label="t('WGPT_SOCIAL_MEDIA_POSTS.DETAILS.ERROR')"
              />
            </span>
            <div class="flex items-center gap-0.5">
              <div
                class="flex items-center h-8 gap-2 px-2 me-2 rounded-lg bg-n-alpha-2 dark:bg-n-solid-2"
              >
                <ChannelIcon
                  :inbox="channelIconInbox"
                  class="flex-shrink-0 size-4"
                />
                <span
                  class="text-sm text-n-slate-12"
                  :class="{ 'italic text-n-slate-11': !inbox }"
                >
                  {{
                    inbox?.name || t('WGPT_SOCIAL_MEDIA_POSTS.DELETED_INBOX')
                  }}
                </span>
              </div>
              <div
                class="flex items-center h-8 gap-2 px-2 me-2 rounded-lg bg-n-alpha-2 dark:bg-n-solid-2"
              >
                <Icon
                  :icon="postTypeIconName"
                  class="flex-shrink-0 size-4 text-n-slate-11"
                />
                <span class="text-sm text-n-slate-12">{{ postTypeText }}</span>
              </div>
            </div>
          </div>

          <div class="flex flex-col gap-6">
            <div class="flex flex-col gap-8">
              <div>
                <span class="py-1 text-sm font-medium text-n-slate-12">{{
                  t('WGPT_SOCIAL_MEDIA_POSTS.DETAILS.CAPTION')
                }}</span>
                <p class="break-words mb-0.5">
                  {{ post.caption }}
                </p>
              </div>

              <div v-if="post.published_at">
                <span class="py-1 text-sm font-medium text-n-slate-12">{{
                  t('WGPT_SOCIAL_MEDIA_POSTS.DETAILS.PUBLISHED_AT')
                }}</span>
                <p class="break-words mb-0.5">
                  {{ formatDate(post.published_at) }}
                </p>
              </div>

              <div v-if="externalUrl">
                <span class="py-1 text-sm font-medium text-n-slate-12">{{
                  t('WGPT_SOCIAL_MEDIA_POSTS.DETAILS.EXTERNAL_URL')
                }}</span>
                <div v-if="externalUrl">
                  <a
                    :href="externalUrl"
                    target="_blank"
                    rel="noopener nofollow noreferrer"
                    class="inline-flex items-center gap-1 hover:underline"
                  >
                    <span>{{ externalUrl }}</span>
                    <span class="i-lucide-external-link text-sm" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <GalleryView
      v-if="showGallery"
      v-model:show="showGallery"
      :attachment="selectedAttachment"
      :all-attachments="mediaItems"
      @close="showGallery = false"
    />

    <Dialog
      ref="removeDialogRef"
      type="alert"
      :title="t('WGPT_SOCIAL_MEDIA_POSTS.REMOVE.TITLE')"
      :description="t('WGPT_SOCIAL_MEDIA_POSTS.REMOVE.DESCRIPTION')"
      :confirm-button-label="t('WGPT_SOCIAL_MEDIA_POSTS.REMOVE.CONFIRM')"
      :is-loading="isRemoving"
      :disable-confirm-button="isRemoving"
      @confirm="handleRemove"
    />
  </section>
</template>
